<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  eventEndpoint: {
    type: Object,
    required: true
  },
  isTestingEvent: {
    type: Boolean,
    default: false
  },
  testingEndpoint: {
    type: Object,
    default: null
  },
  expandedLogRows: {
    type: Set,
    required: true
  }
})

const emit = defineEmits([
  'edit-endpoint',
  'test-endpoint',
  'toggle-logs',
  'show-logs',
  'toggle-status',
  'switch-local',
  'remove-endpoint',
  'open-response-builder'
])

const expandedMoreActions = ref(null)

const toggleMoreActionsDropdown = (eventKey) => {
  if (expandedMoreActions.value === eventKey) {
    expandedMoreActions.value = null
  } else {
    expandedMoreActions.value = eventKey
  }
}

const closeDropdown = () => {
  expandedMoreActions.value = null
}

const handleEditEndpoint = () => {
  emit('edit-endpoint', props.eventEndpoint)
  closeDropdown()
}

const handleTestEndpoint = () => {
  emit('test-endpoint', props.eventEndpoint)
}

const handleToggleLogs = () => {
  emit('toggle-logs', props.eventEndpoint.eventKey)
  closeDropdown()
}

const handleShowLogs = () => {
  emit('show-logs', props.eventEndpoint)
  closeDropdown()
}

const handleToggleStatus = () => {
  emit('toggle-status', props.eventEndpoint.eventKey)
  closeDropdown()
}

const handleSwitchLocal = () => {
  emit('switch-local', props.eventEndpoint)
  closeDropdown()
}

const handleRemoveEndpoint = () => {
  emit('remove-endpoint', props.eventEndpoint.eventKey)
  closeDropdown()
}

const handleOpenResponseBuilder = () => {
  emit('open-response-builder', props.eventEndpoint)
  closeDropdown()
}

// Handle click outside to close dropdown
const handleClickOutside = (event) => {
  if (expandedMoreActions.value) {
    const isInsideDropdown = event.target.closest('.dropdown-actions')
    if (!isInsideDropdown) {
      expandedMoreActions.value = null
    }
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div class="flex items-center justify-center gap-2">
    <!-- Primary Action Button -->
    <button
      @click="handleEditEndpoint"
      class="px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg border border-blue-200 transition-colors flex items-center gap-1"
      :title="eventEndpoint.endpointUrl ? 'Edit endpoint' : 'Add endpoint'"
    >
      <i :class="eventEndpoint.endpointUrl ? 'fas fa-edit' : 'fas fa-plus'"></i>
      {{ eventEndpoint.endpointUrl ? 'Edit' : 'Add' }}
    </button>

    <!-- Test Button -->
    <button
      @click="handleTestEndpoint"
      :disabled="isTestingEvent || !eventEndpoint.isActive"
      class="px-3 py-1.5 text-xs font-medium text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg border border-green-200 transition-colors disabled:opacity-50 flex items-center gap-1"
      :title="eventEndpoint.endpointUrl === '/api/webhook-test' ? 'Test with local webhook receiver' : 'Test with configured endpoint'"
    >
      <i v-if="testingEndpoint?.eventKey === eventEndpoint.eventKey" class="fas fa-spinner fa-spin"></i>
      <i v-else-if="eventEndpoint.endpointUrl === '/api/webhook-test'" class="fas fa-server"></i>
      <i v-else class="fas fa-paper-plane"></i>
      Test
    </button>

    <!-- More Actions Dropdown -->
    <div v-if="eventEndpoint.endpointUrl" class="relative dropdown-actions">
      <button
        @click="toggleMoreActionsDropdown(eventEndpoint.eventKey)"
        class="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors flex items-center gap-1"
        title="More actions"
      >
        <i class="fas fa-ellipsis-v"></i>
      </button>
      
      <div 
        v-if="expandedMoreActions === eventEndpoint.eventKey"
        class="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10"
      >
        <div class="py-1">
          <button
            @click="handleToggleLogs"
            class="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <i :class="expandedLogRows.has(eventEndpoint.eventKey) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="text-xs"></i>
            {{ expandedLogRows.has(eventEndpoint.eventKey) ? 'Hide logs' : 'Show logs' }}
          </button>
          
          <button
            @click="handleShowLogs"
            class="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <i class="fas fa-external-link-alt text-xs"></i>
            View detailed logs
          </button>
          
          <button
            @click="handleToggleStatus"
            class="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <i :class="eventEndpoint.isActive ? 'fas fa-pause' : 'fas fa-play'" class="text-xs"></i>
            {{ eventEndpoint.isActive ? 'Disable' : 'Enable' }}
          </button>
          
          <button
            v-if="eventEndpoint.endpointUrl !== '/api/webhook-test'"
            @click="handleSwitchLocal"
            class="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <i class="fas fa-server text-xs"></i>
            Switch to local receiver
          </button>
          
          <button
            v-if="eventEndpoint.eventKey === 'fixture.created'"
            @click="handleOpenResponseBuilder"
            class="w-full px-3 py-2 text-left text-sm text-blue-700 hover:bg-blue-50 flex items-center gap-2"
          >
            <i class="fas fa-cogs text-xs"></i>
            Response Builder
          </button>
          
          <hr class="my-1">
          
          <button
            @click="handleRemoveEndpoint"
            class="w-full px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50 flex items-center gap-2"
          >
            <i class="fas fa-trash text-xs"></i>
            Remove endpoint
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
